import { FileText } from "lucide-react";
import { type MouseEvent, useState } from "react";

import { cn } from "../../../../lib/cn";
import { Pressable } from "../../../Pressable";
import { ShimmerLoadingOverlay } from "../../../shimmer/ShimmerLoading";
import { Label } from "../../../typography/Label";
import { useChat } from "../../ChatContext";
import { formatChatLabel } from "../../labels";
import { hasBubbleContent } from "../Content";
import { useMessage } from "../Context";
import { MessageRow } from "../Row";
import { BubbleSurface } from "../Surface";
import type { ChatAttachment, ChatSender } from "../types";
import { AudioPlayer } from "./AudioPlayer";
import { ImageLightbox } from "./ImageLightbox";

type AttachmentKind = "file" | "image" | "audio";

const ATTACHMENT_KIND_ORDER: AttachmentKind[] = ["file", "image", "audio"];

export function Attachments() {
    const message = useMessage();
    const groupedAttachments = groupAttachments(message.attachments);
    const items = flattenAttachments(groupedAttachments);
    const hasCompanionBubble = hasBubbleContent(message);
    const anchorKey =
        !hasCompanionBubble && message.showSenderRow
            ? (items.at(-1)?.key ?? null)
            : null;

    if (items.length === 0) {
        return null;
    }

    return (
        <div
            className={cn(
                "flex max-w-full flex-col gap-4",
                message.sender === "member" ? "items-end" : "items-start",
            )}
        >
            {items.map((item) => (
                <AttachmentBubble
                    key={item.key}
                    item={item}
                    sender={message.sender}
                    isAnchor={item.key === anchorKey}
                />
            ))}
        </div>
    );
}

function AttachmentBubble({
    item,
    sender,
    isAnchor,
}: {
    item: RenderableAttachment;
    sender: ChatSender;
    isAnchor: boolean;
}) {
    const surface = (
        <BubbleSurface
            spacing={item.kind === "image" ? "image" : "content"}
            shape={isAnchor ? "message" : "rounded"}
        >
            <Attachment attachment={item.attachment} kind={item.kind} />
        </BubbleSurface>
    );

    if (isAnchor && sender === "agent") {
        return <MessageRow>{surface}</MessageRow>;
    }

    return <div className={sender === "agent" ? "pl-32" : undefined}>{surface}</div>;
}

function Attachment({
    attachment,
    kind,
}: {
    attachment: ChatAttachment;
    kind: AttachmentKind;
}) {
    const { labels } = useChat();

    switch (kind) {
        case "file":
            return (
                <Pressable
                    className="text-left"
                    aria-label={formatChatLabel(labels.downloadFile, {
                        filename: attachment.filename ?? labels.fileAttachmentFallback,
                    })}
                    onPress={attachment.onPress}
                >
                    <div className="flex w-full flex-row items-center gap-8">
                        <div className="-my-8 -ml-8 box-border flex size-56 min-h-56 min-w-56 items-center justify-center rounded-[8px] bg-component-chat-document-background">
                            <FileText
                                aria-hidden="true"
                                className="size-24 text-component-chat-document-icon"
                            />
                        </div>

                        <div className="flex w-full flex-col items-start gap-4">
                            <Label size="small" weight="bold">
                                {attachment.filename}
                            </Label>

                            <Label
                                size="small"
                                weight="bold"
                                color="tertiary"
                                className="trim"
                            >
                                {attachment.mimeType === "application/pdf"
                                    ? "PDF"
                                    : attachment.mimeType}
                            </Label>
                        </div>
                    </div>
                </Pressable>
            );

        case "image":
            return <ImageAttachment attachment={attachment} />;

        case "audio":
            return <AudioPlayer attachment={attachment} />;

        default: {
            const exhaustiveKind: never = kind;
            return exhaustiveKind;
        }
    }
}

function ImageAttachment({ attachment }: { attachment: ChatAttachment }) {
    const { labels, onOpenImage } = useChat();
    const [originRect, setOriginRect] = useState<{
        top: number;
        left: number;
        width: number;
        height: number;
    } | null>(null);
    const [loadedUrl, setLoadedUrl] = useState<string>();
    const [naturalSize, setNaturalSize] = useState<{
        width: number;
        height: number;
    } | null>(null);

    const filename = attachment.filename ?? labels.imageAttachmentFallback;
    const alt = formatChatLabel(labels.imageAttachmentAlt, { filename });
    const isReady = !!attachment.url && loadedUrl === attachment.url && naturalSize;

    function handleOpen(event: MouseEvent<HTMLButtonElement>) {
        if (!attachment.url || !isReady || !naturalSize) {
            return;
        }

        // The host showed it in its own viewer.
        if (onOpenImage?.(attachment.url)) {
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        setOriginRect({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        });
    }

    function markReady(image: HTMLImageElement) {
        if (
            !attachment.url ||
            !image.complete ||
            image.naturalWidth <= 0 ||
            (loadedUrl === attachment.url && naturalSize)
        ) {
            return;
        }

        setLoadedUrl(attachment.url);
        setNaturalSize({
            width: image.naturalWidth,
            height: image.naturalHeight,
        });
    }

    return (
        <>
            <Pressable
                className="block w-[320px] max-w-full leading-none"
                aria-label={formatChatLabel(labels.openImage, { filename })}
                aria-busy={!isReady}
                isDisabled={!isReady}
                onPress={handleOpen}
            >
                <ShimmerLoadingOverlay
                    isReady={!!isReady}
                    className="aspect-[4/3] max-h-[420px] w-full"
                >
                    <img
                        src={attachment.url}
                        alt={alt}
                        loading="lazy"
                        decoding="async"
                        className="size-full object-cover"
                        ref={(image) => {
                            if (image) {
                                markReady(image);
                            }
                        }}
                        onLoad={(event) => {
                            markReady(event.currentTarget);
                        }}
                    />
                </ShimmerLoadingOverlay>
            </Pressable>

            {originRect && attachment.url && naturalSize ? (
                <ImageLightbox
                    src={attachment.url}
                    alt={alt}
                    originRect={originRect}
                    naturalWidth={naturalSize.width}
                    naturalHeight={naturalSize.height}
                    onClose={() => setOriginRect(null)}
                />
            ) : null}
        </>
    );
}

function groupAttachments(
    attachments: ChatAttachment[],
): Record<AttachmentKind, ChatAttachment[]> {
    const groupedAttachments: Record<AttachmentKind, ChatAttachment[]> = {
        file: [],
        image: [],
        audio: [],
    };

    for (const attachment of attachments) {
        if (attachment.mimeType.startsWith("image")) {
            groupedAttachments.image.push(attachment);
        } else if (attachment.mimeType.startsWith("audio")) {
            groupedAttachments.audio.push(attachment);
        } else {
            groupedAttachments.file.push(attachment);
        }
    }

    return groupedAttachments;
}

type RenderableAttachment = {
    key: string;
    kind: AttachmentKind;
    attachment: ChatAttachment;
};

function flattenAttachments(
    groupedAttachments: Record<AttachmentKind, ChatAttachment[]>,
): RenderableAttachment[] {
    const items: RenderableAttachment[] = [];

    for (const kind of ATTACHMENT_KIND_ORDER) {
        for (const [index, attachment] of groupedAttachments[kind].entries()) {
            if (kind === "audio" && !attachment.url) {
                continue;
            }

            items.push({
                key: `${kind}-${attachment.filename ?? index}`,
                kind,
                attachment,
            });
        }
    }

    return items;
}
